// src/modules/patients.js — Patient linking: picker (search + create) + link/unlink.
// Wires to the FEAT-001 HTTP contract:
//   GET  /patients?q=&limit=       -> { patients: [{patientId, patientLabel, updatedAt}], count }
//   POST /patients {patientLabel}  -> { patientId, patientLabel }  (label is SANITIZED server-side)
//   POST /notes/{id}/link {patientId}        -> link   (patientId: uuid)
//   POST /notes/{id}/link {patientId:null}   -> unlink
// patientLabel is PHI — escapeHtml before render, never log. patientId is a safe opaque id.
import { escapeHtml, isValidUuid } from '../utils/dom.js';
import { apiFetchWithRefresh } from './debug.js';

const SEARCH_DEBOUNCE_MS = 300;

/** POST /patients — mint a patient from a typed label. Returns {patientId, patientLabel}. Throws on error. */
export async function createPatient(label) {
    const res = await apiFetchWithRefresh('/patients', {
        method: 'POST',
        body: JSON.stringify({ patientLabel: label }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Could not create patient');
    }
    return res.json();
}

/**
 * GET /patients/{id}/summary — on-demand pre-visit summary.
 * Returns { summary: string|null, sourceNotes: [{noteId, createdAt, patientLabel}], cached, skippedNotes? }.
 * summary === null means no linked history yet (handle gracefully, not an error).
 */
export async function fetchPatientSummary(patientId, notesOnly) {
    if (!isValidUuid(patientId)) throw new Error('Could not load summary');
    // B2: notesOnly=1 returns { summary:null, sourceNotes:[...] } with NO Bedrock call —
    // the cheap list-only preview shown when a patient is selected at record time.
    const path = '/patients/' + patientId + '/summary' + (notesOnly ? '?notesOnly=1' : '');
    const res = await apiFetchWithRefresh(path);
    if (!res.ok) throw new Error('Could not load summary');
    return res.json();
}

/** POST /notes/{id}/link {patientId} — link a note to an existing patient. */
export async function linkNote(noteId, patientId) {
    if (!isValidUuid(noteId) || !isValidUuid(patientId)) throw new Error('Could not link');
    const res = await apiFetchWithRefresh('/notes/' + noteId + '/link', {
        method: 'POST',
        body: JSON.stringify({ patientId: patientId }),
    });
    if (!res.ok) {
        // 404 deliberately conflates note-not-owned and patient-not-in-office — generic message.
        throw new Error('Could not link');
    }
    return res.json();
}

/**
 * POST /patients/{patientId}/link-notes {noteIds} — one-time bulk link (B3).
 * Returns { patientId, linked:[noteId,...], failed:[{noteId, reason},...] }.
 * A partial batch is still a 200 (per-note failures land in `failed`); throws only on a
 * non-ok HTTP status, with a generic PHI-free message. Validates ids client-side first.
 */
export async function bulkLinkNotes(patientId, noteIds) {
    if (!isValidUuid(patientId)) throw new Error('Could not link notes');
    const ids = (noteIds || []).filter(isValidUuid);
    if (!ids.length) throw new Error('Could not link notes');
    const res = await apiFetchWithRefresh('/patients/' + patientId + '/link-notes', {
        method: 'POST',
        body: JSON.stringify({ noteIds: ids }),
    });
    if (!res.ok) throw new Error('Could not link notes');
    return res.json();
}

/** POST /notes/{id}/link {patientId:null} — unlink a note (reversible). */
export async function unlinkNote(noteId) {
    if (!isValidUuid(noteId)) throw new Error('Could not unlink');
    const res = await apiFetchWithRefresh('/notes/' + noteId + '/link', {
        method: 'POST',
        body: JSON.stringify({ patientId: null }),
    });
    if (!res.ok) throw new Error('Could not unlink');
    return res.json();
}

/**
 * Distinct historical patientLabel suggestions for the unified field (B1).
 * Pure + reusable: derives distinct labels from an already-loaded notes list
 * (getAllLoadedNotes()), filtered by the typed query (case-insensitive substring),
 * EXCLUDING any label that already resolves to a roster patient (those appear as
 * roster rows instead). Case/whitespace variants are NOT merged — each distinct
 * label string is its own suggestion (NFR-1 anti-false-merge disambiguation).
 *   notes        : [{patientLabel, ...}] from getAllLoadedNotes()
 *   query        : the typed text
 *   rosterLabels : labels already shown as roster rows (lowercased set/array)
 * Returns [{ patientLabel, count }] newest-order-agnostic, de-duplicated.
 * ponytail: in-memory distinct-label scan over loaded notes; ceiling = loaded
 * page only, not full history; upgrade path = a label-suggest endpoint.
 */
export function historyLabelSuggestions(notes, query, rosterLabels) {
    const q = (query || '').trim().toLowerCase();
    if (!q || !Array.isArray(notes)) return [];
    const excluded = new Set((rosterLabels || []).map(s => (s || '').trim().toLowerCase()));
    const counts = new Map(); // exact label string -> count (preserves case/whitespace variants)
    for (const n of notes) {
        const label = n && n.patientLabel;
        if (!label) continue;
        const lower = label.trim().toLowerCase();
        if (!lower.includes(q)) continue;      // query filter
        if (excluded.has(lower)) continue;      // already a roster row
        counts.set(label, (counts.get(label) || 0) + 1);
    }
    const out = [];
    for (const [patientLabel, count] of counts) out.push({ patientLabel, count });
    return out;
}

/**
 * Attach a reusable patient picker to a host element. The host must contain:
 *   - a results   [data-patient-results]  (a Bootstrap list-group container)
 *   - a selection [data-patient-selected] (shows the chosen patient + clear button)
 * and EITHER an internal [data-patient-search] input, OR an external search input
 * passed as opts.searchInput (B1 unified field: the free-text #patientLabel drives
 * the dropdown, so the doctor sees ONE control instead of two).
 *
 * opts.onChange(patientId|null)     fires whenever the selection changes.
 * opts.searchInput                  external <input> to use as the search field
 *                                   (its .value also carries the note's patientLabel).
 * opts.extraSuggestions(query)      -> [{patientLabel, count}] history rows under a
 *                                   "From past notes" divider (B1 label-history).
 * Returns a controller: { getPatientId(), reset(), destroy() }.
 */
export function attachPatientPicker(host, opts) {
    opts = opts || {};
    // External search input (unified field) or the picker's own one.
    const input = opts.searchInput || host.querySelector('[data-patient-search]');
    const results = host.querySelector('[data-patient-results]');
    const selected = host.querySelector('[data-patient-selected]');
    const external = !!opts.searchInput; // #patientLabel is the label carrier — never wipe its value
    let selectedId = null;
    let debounceTimer = null;
    let reqSeq = 0; // guards against out-of-order responses

    function emit() { if (typeof opts.onChange === 'function') opts.onChange(selectedId); }

    function hideResults() {
        results.innerHTML = '';
        results.classList.add('d-none');
    }

    function showSelected(patientId, label) {
        selectedId = patientId;
        input.classList.add('d-none');
        // Keep the label carrier populated so startRecording/upload still reads it
        // from #patientLabel even while the raw input is hidden behind the chip.
        if (external) input.value = label;
        hideResults();
        selected.innerHTML =
            '<div class="input-group input-group-sm">' +
            '<span class="input-group-text flex-grow-1 text-start text-truncate">&#128100; ' + escapeHtml(label) + '</span>' +
            '<button type="button" class="btn btn-outline-secondary" data-patient-clear title="Clear linked patient" style="min-height:44px">&times;</button>' +
            '</div>';
        selected.classList.remove('d-none');
        emit();
    }

    function clearSelection() {
        selectedId = null;
        selected.innerHTML = '';
        selected.classList.add('d-none');
        input.classList.remove('d-none');
        input.value = '';
        hideResults();
        if (external) input.focus();
        emit();
    }

    function renderError(msg) {
        results.innerHTML =
            '<div class="list-group-item list-group-item-danger small">' + escapeHtml(msg) + '</div>';
        results.classList.remove('d-none');
    }

    function renderLoading() {
        results.innerHTML =
            '<div class="list-group-item small text-muted">' +
            '<span class="spinner-border spinner-border-sm" role="status"></span> Searching...</div>';
        results.classList.remove('d-none');
    }

    function renderResults(patients, query) {
        let html = '';
        // (a) Roster rows — existing patients in dentscribe-patients.
        for (const p of patients) {
            html +=
                '<button type="button" class="list-group-item list-group-item-action" style="min-height:44px" ' +
                'data-patient-pick data-pid="' + escapeHtml(p.patientId) + '" ' +
                'data-plabel="' + escapeHtml(p.patientLabel) + '">&#128100; ' +
                escapeHtml(p.patientLabel) + '</button>';
        }
        // (b) Label-history rows — distinct patientLabels from loaded notes not yet
        // on the roster. Selecting one creates+links a patient in one tap.
        if (typeof opts.extraSuggestions === 'function') {
            const rosterLabels = patients.map(p => p.patientLabel);
            const history = opts.extraSuggestions(query, rosterLabels) || [];
            if (history.length) {
                html +=
                    '<div class="list-group-item disabled small text-muted py-1 bg-light">From past notes</div>';
                for (const h of history) {
                    const countBadge = h.count
                        ? '<span class="badge bg-secondary rounded-pill ms-2">' + h.count + '</span>'
                        : '';
                    html +=
                        '<button type="button" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center" style="min-height:44px" ' +
                        'data-patient-history data-plabel="' + escapeHtml(h.patientLabel) + '">' +
                        '<span class="text-truncate">&#128221; ' + escapeHtml(h.patientLabel) + '</span>' +
                        countBadge + '</button>';
                }
            }
        }
        // (c) New patient row.
        const typed = (query || '').trim();
        if (typed) {
            html +=
                '<button type="button" class="list-group-item list-group-item-action text-primary" style="min-height:44px" ' +
                'data-patient-new data-plabel="' + escapeHtml(typed) + '">' +
                '&#43; New patient: &ldquo;' + escapeHtml(typed) + '&rdquo;</button>';
        }
        if (!html) {
            html = '<div class="list-group-item small text-muted">No matching patients</div>';
        }
        results.innerHTML = html;
        results.classList.remove('d-none');
    }

    async function search(query) {
        const seq = ++reqSeq;
        renderLoading();
        try {
            const res = await apiFetchWithRefresh('/patients?limit=20&q=' + encodeURIComponent(query));
            if (seq !== reqSeq) return; // a newer search superseded this one
            if (!res.ok) { renderError('Could not load patients'); return; }
            const data = await res.json();
            renderResults(data.patients || [], query);
        } catch (err) {
            if (seq !== reqSeq) return;
            renderError('Could not load patients');
        }
    }

    function onInput() {
        const q = input.value.trim();
        clearTimeout(debounceTimer);
        if (!q) { hideResults(); return; }
        debounceTimer = setTimeout(() => search(q), SEARCH_DEBOUNCE_MS);
    }

    async function onResultsClick(e) {
        const pick = e.target.closest('[data-patient-pick]');
        if (pick) {
            showSelected(pick.getAttribute('data-pid'), pick.getAttribute('data-plabel'));
            return;
        }
        // History row OR new-patient row both mint a patient from the label, then
        // select it (create+link in one tap). The field stays editable on failure.
        const createRow = e.target.closest('[data-patient-history], [data-patient-new]');
        if (createRow) {
            const label = createRow.getAttribute('data-plabel');
            createRow.disabled = true;
            createRow.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Adding...';
            try {
                const patient = await createPatient(label);
                showSelected(patient.patientId, patient.patientLabel);
            } catch (err) {
                renderError(err.message || 'Could not create patient');
            }
        }
    }

    function onSelectedClick(e) {
        if (e.target.closest('[data-patient-clear]')) clearSelection();
    }

    input.addEventListener('input', onInput);
    results.addEventListener('click', onResultsClick);
    selected.addEventListener('click', onSelectedClick);

    return {
        getPatientId() { return selectedId; },
        reset() { clearTimeout(debounceTimer); clearSelection(); },
        destroy() {
            clearTimeout(debounceTimer);
            input.removeEventListener('input', onInput);
            results.removeEventListener('click', onResultsClick);
            selected.removeEventListener('click', onSelectedClick);
        },
    };
}
